import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { uploadChecklistProof } from "@/lib/upload";

// Phase 42 Plan 14 (TPRF-03) — canvas signature capture. The CLIENT signs on
// the STAFF member's device; this is a client ACKNOWLEDGEMENT, not staff
// self-attestation, so `signerName` is a required text input that is NEVER
// prefilled with the logged-in staff user (that would quietly turn a client
// signature into a staff self-attestation and destroy the point of the
// artifact — see 42-CONTEXT.md's Proof capture decisions). `signedAt` is
// stamped SERVER-side at tick time (task-checklist-items.ts's markItemDoneTx)
// — this component never sends a client timestamp.
//
// Plain `<canvas>` + pointer events, no dependency — the repo carries no
// signature library today and this is small enough not to need one (Claude's
// Discretion per 42-CONTEXT.md).
//
// Plain markup throughout (local component state only, no react-hook-form)
// — shadcn Form* primitives require a <FormField> ancestor and would
// white-screen this dialog otherwise (the standing repo trap, latest 0e61073,
// see checklists-settings.tsx:1303-1327 for the same pattern applied to
// proofType).

export interface SignatureCapture {
  signatureUrl: string;
  signerName: string;
}

export function SignaturePad({
  onCaptured,
  disabled,
}: {
  /** Fires once the client's stroke is exported to PNG and uploaded through the existing uploadChecklistProof() path — no new storage. */
  onCaptured: (capture: SignatureCapture) => void;
  disabled?: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const [hasStroke, setHasStroke] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [uploading, setUploading] = useState(false);

  // The backing store is scaled for devicePixelRatio so the stroke isn't
  // blurry/offset on retina phones — the CSS box stays a fixed height, only
  // the canvas's internal pixel grid and drawing context scale up with it.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const cssWidth = canvas.clientWidth || 320;
    const cssHeight = 160;
    canvas.width = cssWidth * dpr;
    canvas.height = cssHeight * dpr;
    canvas.style.height = `${cssHeight}px`;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#111827";
  }, []);

  function pointFromEvent(e: React.PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function handlePointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    if (disabled || uploading) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    const ctx = canvas.getContext("2d");
    const { x, y } = pointFromEvent(e);
    ctx?.beginPath();
    ctx?.moveTo(x, y);
  }

  function handlePointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current || disabled || uploading) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx) return;
    const { x, y } = pointFromEvent(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasStroke) setHasStroke(true);
  }

  function handlePointerUp() {
    drawingRef.current = false;
  }

  function handleClear() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasStroke(false);
  }

  async function handleSave() {
    const canvas = canvasRef.current;
    const trimmedName = signerName.trim();
    if (!canvas || !hasStroke || trimmedName.length === 0 || uploading) return;
    setUploading(true);
    try {
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
      if (!blob) {
        toast.error("Could not export the signature — please try again.");
        return;
      }
      const file = new File([blob], "signature.png", { type: "image/png" });
      const result = await uploadChecklistProof(file);
      if (!result.ok) {
        toast.error(result.message || "Signature upload failed.");
        return;
      }
      onCaptured({ signatureUrl: result.url, signerName: trimmedName });
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium">Hand the device to the client to sign.</p>

      <div className="space-y-1">
        <label htmlFor="signer-name" className="text-xs font-medium text-muted-foreground">
          Client name <span className="text-destructive">*</span>
        </label>
        <Input
          id="signer-name"
          value={signerName}
          onChange={(e) => setSignerName(e.target.value)}
          placeholder="e.g. Rohan Mehta"
          disabled={disabled || uploading}
          autoComplete="off"
        />
      </div>

      <canvas
        ref={canvasRef}
        className="w-full touch-none rounded-md border bg-white"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      />

      <div className="flex items-center justify-between gap-2">
        <Button type="button" variant="outline" size="sm" onClick={handleClear} disabled={disabled || uploading || !hasStroke}>
          Clear
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={disabled || uploading || !hasStroke || signerName.trim().length === 0}
        >
          {uploading ? "Saving signature…" : "Use signature"}
        </Button>
      </div>
    </div>
  );
}
